import React, { useEffect, useState } from "react";
import {
  Row,
  Col,
  Table,
  Button,
  Spinner,
  Form,
  InputGroup,
  Tabs,
  Tab,
  Pagination,
} from "react-bootstrap";
import axios from "axios";
import Swal from "sweetalert2";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import API_BASE_URL from "../config";

export default function ManageBillingTaxes() {
  const [companies, setCompanies] = useState([]);
  const [selectedCompany, setSelectedCompany] = useState(() => {
    const storedCompany = localStorage.getItem("selectedCompany");
    return storedCompany ? JSON.parse(storedCompany).id : "";
  });
  const [branches, setBranches] = useState([]);
  const [branchId, setBranchId] = useState(() => {
    const storedBranch = localStorage.getItem("selectedBranch");
    return storedBranch ? JSON.parse(storedBranch).id : "";
  });
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [taxes, setTaxes] = useState([]);
  const [invoicesTotals, setInvoicesTotals] = useState({
    total_amount: 0,
    tax: 0,
    discount: 0,
  });
  const [taxesTotals, setTaxesTotals] = useState({ tax: 0 });
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [activeTab, setActiveTab] = useState("bills");

  // Load companies on mount
  useEffect(() => {
    async function fetchCompanies() {
      try {
        const token = localStorage.getItem("token");
        const res = await axios.get(`${API_BASE_URL}/companies`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const companyData = res.data || [];
        setCompanies(companyData);

        // Validate stored selectedCompany
        const storedCompany = localStorage.getItem("selectedCompany");
        if (storedCompany) {
          const parsedCompany = JSON.parse(storedCompany);
          const isValidCompany = companyData.some((c) => c.id === parsedCompany.id);
          if (!isValidCompany) {
            localStorage.removeItem("selectedCompany");
            setSelectedCompany("");
            localStorage.removeItem("selectedBranch");
            setBranchId("");
          }
        }
      } catch (err) {
        console.error("fetchCompanies", err);
        Swal.fire("Error", "Failed to load companies", "error");
        setSelectedCompany("");
        setBranchId("");
      }
    }
    fetchCompanies();
  }, []);

  // Load branches when selectedCompany changes
  useEffect(() => {
    if (!selectedCompany) {
      setBranches([]);
      setBranchId("");
      return;
    }

    async function fetchBranches() {
      try {
        const res = await axios.get(
          `${API_BASE_URL}/companies/${selectedCompany}/branches`
        );
        const branchData = res.data || [];
        setBranches(branchData);

        // Validate stored selectedBranch
        const storedBranch = localStorage.getItem("selectedBranch");
        if (storedBranch) {
          const parsedBranch = JSON.parse(storedBranch);
          const isValidBranch = branchData.some((b) => b.id === parsedBranch.id);
          if (!isValidBranch) {
            localStorage.removeItem("selectedBranch");
            setBranchId("");
          } else {
            setBranchId(parsedBranch.id);
          }
        }
      } catch (err) {
        console.error("fetchBranches", err);
        Swal.fire("Error", "Failed to load branches", "error");
        setBranchId("");
      }
    }
    fetchBranches();
  }, [selectedCompany]);

  // Fetch invoices
  const fetchInvoices = async (pageNo = 1) => {
    if (!selectedCompany) {
      setInvoices([]);
      setInvoicesTotals({ total_amount: 0, tax: 0, discount: 0 });
      setTotalPages(1);
      return;
    }
    setLoading(true);
    try {
      const params = {
        page: pageNo,
        pageSize,
        branch_id: branchId || undefined,
        start_date: startDate ? startDate.toISOString().split("T")[0] : undefined,
        end_date: endDate ? endDate.toISOString().split("T")[0] : undefined,
      };
      const res = await axios.get(
        `${API_BASE_URL}/companies/${selectedCompany}/invoices`,
        { params }
      );
      setInvoices(res.data.invoices || []);
      setInvoicesTotals(res.data.totals || { total_amount: 0, tax: 0, discount: 0 });
      setTotalPages(res.data.totalPages || 1);
      setPage(pageNo);
    } catch (err) {
      console.error("fetchInvoices", err);
      Swal.fire("Error", "Failed to load invoices", "error");
      setInvoices([]);
      setInvoicesTotals({ total_amount: 0, tax: 0, discount: 0 });
    } finally {
      setLoading(false);
    }
  };

  // Fetch taxes
  const fetchTaxes = async (pageNo = 1) => {
    if (!selectedCompany) {
      setTaxes([]);
      setTaxesTotals({ tax: 0 });
      return;
    }
    try {
      const params = {
        page: pageNo,
        pageSize,
        branch_id: branchId || undefined,
        start_date: startDate ? startDate.toISOString().split("T")[0] : undefined,
        end_date: endDate ? endDate.toISOString().split("T")[0] : undefined,
      };
      const res = await axios.get(
        `${API_BASE_URL}/companies/${selectedCompany}/taxes`,
        { params }
      );
      setTaxes(res.data.taxes || []);
      setTaxesTotals(res.data.totals || { tax: 0 });
    } catch (err) {
      console.error("fetchTaxes", err);
      Swal.fire("Error", "Failed to load taxes", "error");
      setTaxes([]);
      setTaxesTotals({ tax: 0 });
    }
  };

  // Handle search button click
  const handleSearch = () => {
    fetchInvoices(1);
    fetchTaxes(1);
  };

  return (
    <div className="container mt-4">
      <h4 className="mb-4">Manage Billing & Taxes</h4>

      {/* Filters */}
      <Row className="mb-3 gy-3 align-items-end">
        <Col xs={12} md={3}>
          <Form.Group>
            <Form.Label>Company</Form.Label>
            <Form.Select
              value={selectedCompany}
              onChange={(e) => {
                setSelectedCompany(e.target.value);
                setBranchId("");
                localStorage.removeItem("selectedBranch");
              }}
            >
              <option value="">Select Company</option>
              {companies.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </Form.Select>
          </Form.Group>
        </Col>
        <Col xs={12} md={3}>
          <Form.Group>
            <Form.Label>Branch</Form.Label>
            <Form.Select
              value={branchId}
              onChange={(e) => {
                const selectedBranch = branches.find((b) => b.id === e.target.value);
                setBranchId(e.target.value);
                localStorage.setItem("selectedBranch", JSON.stringify(selectedBranch || {}));
              }}
              disabled={!branches.length}
            >
              <option value="">All Branches</option>
              {branches.map((b) => (
                <option key={b.id} value={b.id}>
                  {b.name} ({b.city})
                </option>
              ))}
            </Form.Select>
          </Form.Group>
        </Col>
        <Col xs={12} md={2}>
          <Form.Group>
            <Form.Label>From Date</Form.Label>
            <DatePicker
              selected={startDate}
              onChange={(date) => setStartDate(date)}
              className="form-control"
              placeholderText="Select start date"
            />
          </Form.Group>
        </Col>
        <Col xs={12} md={2}>
          <Form.Group>
            <Form.Label>To Date</Form.Label>
            <DatePicker
              selected={endDate}
              onChange={(date) => setEndDate(date)}
              className="form-control"
              placeholderText="Select end date"
            />
          </Form.Group>
        </Col>
        <Col xs={12} md={2}>
          <Button variant="primary" onClick={handleSearch}>
            <i className="bi bi-search me-2"></i>Search
          </Button>
        </Col>
      </Row>

      {/* Tabs */}
      <Tabs
        activeKey={activeTab}
        onSelect={(k) => setActiveTab(k)}
        className="mb-3"
      >
        <Tab eventKey="bills" title="Bills Generated">
          {loading ? (
            <div className="text-center py-5">
              <Spinner animation="border" />
            </div>
          ) : (
            <>
              <div className="table-responsive">
                <Table striped bordered hover size="sm" className="align-middle">
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>Customer</th>
                      <th>Branch</th>
                      <th>Services</th>
                      <th>Total Amount</th>
                      <th>Tax</th>
                      <th>Discount</th>
                      <th>Status</th>
                      <th>Created At</th>
                    </tr>
                  </thead>
                  <tbody>
                    {invoices.length === 0 ? (
                      <tr>
                        <td colSpan="9" className="text-center">
                          No invoices found
                        </td>
                      </tr>
                    ) : (
                      invoices.map((inv, i) => (
                        <tr key={inv.id}>
                          <td>{(page - 1) * pageSize + i + 1}</td>
                          <td>{inv.customer_name || "-"}</td>
                          <td>{inv.branch_name || "All"}</td>
                          <td style={{ minWidth: 200 }}>
                            {inv.service_names && inv.service_names.length > 0
                              ? inv.service_names.join(", ")
                              : "-"}
                          </td>
                          <td>₹{(inv.total_amount_cents || 0).toFixed(2)}</td>
                          <td>₹{(inv.tax_cents || 0).toFixed(2)}</td>
                          <td>₹{(inv.discount_cents || 0).toFixed(2)}</td>
                          <td>{inv.status}</td>
                          <td>
                            {inv.created_at
                              ? new Date(inv.created_at).toLocaleString()
                              : "-"}
                          </td>
                        </tr>
                      ))
                    )}
                    {/* Grand Total Row */}
                    {invoices.length > 0 && (
                      <tr className="table-secondary fw-bold">
                        <td colSpan="4" className="text-end">
                          Grand Total:
                        </td>
                        <td>₹{(invoicesTotals.total_amount || 0).toFixed(2)}</td>
                        <td>₹{(invoicesTotals.tax || 0).toFixed(2)}</td>
                        <td>₹{(invoicesTotals.discount || 0).toFixed(2)}</td>
                        <td colSpan="2"></td>
                      </tr>
                    )}
                  </tbody>
                </Table>
              </div>

              {totalPages > 1 && (
                <div className="d-flex justify-content-center mt-3">
                  <Pagination>
                    {Array.from({ length: totalPages }).map((_, idx) => (
                      <Pagination.Item
                        key={idx}
                        active={page === idx + 1}
                        onClick={() => fetchInvoices(idx + 1)}
                      >
                        {idx + 1}
                      </Pagination.Item>
                    ))}
                  </Pagination>
                </div>
              )}
            </>
          )}
        </Tab>
        <Tab eventKey="taxes" title="Taxes Collected">
          {loading ? (
            <div className="text-center py-5">
              <Spinner animation="border" />
            </div>
          ) : (
            <div className="table-responsive">
              <Table striped bordered hover size="sm" className="align-middle">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Invoice ID</th>
                    <th>Customer</th>
                    <th>Branch</th>
                    <th>Services</th>
                    <th>Tax Amount</th>
                    <th>Created At</th>
                  </tr>
                </thead>
                <tbody>
                  {taxes.length === 0 ? (
                    <tr>
                      <td colSpan="7" className="text-center">
                        No taxes found
                      </td>
                    </tr>
                  ) : (
                    taxes.map((tax, i) => (
                      <tr key={tax.id}>
                        <td>{i + 1}</td>
                        <td>{tax.invoice_id}</td>
                        <td>{tax.customer_name || "-"}</td>
                        <td>{tax.branch_name || "All"}</td>
                        <td style={{ minWidth: 200 }}>
                          {tax.service_names && tax.service_names.length > 0
                            ? tax.service_names.join(", ")
                            : "-"}
                        </td>
                        <td>₹{(tax.tax_cents || 0).toFixed(2)}</td>
                        <td>
                          {tax.created_at
                            ? new Date(tax.created_at).toLocaleString()
                            : "-"}
                        </td>
                      </tr>
                    ))
                  )}
                  {taxes.length > 0 && (
                    <tr className="table-secondary fw-bold">
                      <td colSpan="5" className="text-end">
                        Total Tax Collected:
                      </td>
                      <td>₹{(taxesTotals.tax || 0).toFixed(2)}</td>
                      <td></td>
                    </tr>
                  )}
                </tbody>
              </Table>
            </div>
          )}
        </Tab>
      </Tabs>
    </div>
  );
}